import type { StopView } from "../src/stop-notice-contracts.ts";
/**
 * The card for a reply the app wrote no releasable draft for: its checks refused every draft, or
 * drafting failed part way (docs/2026-10-05-withheld-card.md).
 *
 * It says what happened, names what the checks found, keeps the promise that nothing unreviewed is
 * released, and offers the one route the app has: the rep writes the reply. It has no button that
 * only marks the step done, because doing that answers nobody.
 *
 * Where a check refused particular words, they are quoted above its reason and marked as Dawid
 * worded it (ledger `abd7ff5a`): "Not sent — refused because…". A quote is evidence, never reply
 * text: nothing offers to copy it or put it in the reply box.
 */
import type { WithheldReason, WithheldView } from "../src/withheld-view.ts";
import { EditIcon } from "./icons.tsx";

const plural = (count: number, one: string, many: string): string =>
  `${count} ${count === 1 ? one : many}`;

/** "It said…" as the end of "refused because it said…". Every check's words open with a plain word. */
const because = (text: string): string => `${text.charAt(0).toLowerCase()}${text.slice(1)}`;

/** One refusing check: its words alone, or the words it refused and why. */
function Reason({ reason }: { reason: WithheldReason }) {
  if (reason.refused.length === 0) return <li>{reason.text}</li>;
  return (
    <li className="lcr-withheld-refused">
      {reason.refused.map((quote) => (
        // `dir="auto"`: a draft written right to left is quoted the way it was written.
        <blockquote key={quote} className="lcr-withheld-quote" dir="auto">
          “{quote}”
        </blockquote>
      ))}
      <p className="lcr-withheld-because">
        <strong className="lcr-withheld-not-sent">Not sent</strong> — refused because{" "}
        {because(reason.text)}
      </p>
    </li>
  );
}

function happened(view: WithheldView): string {
  if (view.kind === "failed") return "Drafting this reply failed part way, so there is no draft.";
  if (view.kind === "stopped")
    return "The app’s own checks stopped this reply before it could show you a draft.";
  return view.attempts === null
    ? "The app’s own checks refused every draft it wrote for this reply."
    : `The app wrote ${plural(view.attempts, "draft", "drafts")} for this reply and its own checks refused ${view.attempts === 1 ? "it" : "each one"}.`;
}

export function WithheldCard({
  stepId,
  view,
  canWrite,
  onWrite,
  stopNotice,
}: {
  stepId: string;
  view: WithheldView;
  /** False on a read-only conversation, where the reply box is not there to open. */
  canWrite: boolean;
  onWrite: () => void;
  stopNotice?: StopView;
}) {
  const headingId = `lcr-withheld-${stepId.replace(/[^\w-]/g, "_")}`;
  return (
    <article className="lcr-sheet lcr-withheld" id={`step-${stepId}`} aria-labelledby={headingId}>
      <div className="lcr-step-heading">
        <h3 id={headingId}>No draft for this reply</h3>
        <span className="lcr-badge">To do</span>
      </div>
      <p className="lcr-withheld-lead">{happened(view)}</p>
      {view.kind === "refused" && (
        <div className="lcr-withheld-reasons">
          <h4>What the checks found</h4>
          <ul>
            {view.reasons.map((reason) => (
              <Reason key={reason.text} reason={reason} />
            ))}
            {view.unnamed > 0 && (
              <li className="lcr-withheld-unnamed">
                {view.reasons.length > 0 ? "And " : ""}
                {plural(view.unnamed, "more check", "more checks")} the app has no description for
                yet.
              </li>
            )}
          </ul>
        </div>
      )}
      {view.kind === "stopped" && (
        <p className="lcr-withheld-note">The run did not record which check stopped it.</p>
      )}
      <p className="lcr-withheld-promise">No unreviewed draft is released.</p>
      {canWrite && (
        <button type="button" onClick={onWrite}>
          <EditIcon size={16} /> Write the reply yourself
        </button>
      )}
      <p className="lcr-withheld-note">
        {stopNotice?.delivery.sentence ?? "This workspace can read the card here in the app."} Your
        own reply closes this card.
      </p>
    </article>
  );
}
